import { useState } from "react";
import { Button } from "@/shared/ui/button";
import type { Severity } from "../types";
import { SeverityPicker } from "./SeverityPicker";

/**
 * Variant A (anchored): our assessment is shown; the client reacts with the
 * traffic-light verdict. 🟡 "wrong severity" opens the severity picker. The
 * semantic colors sit on top of the shared Button.
 *
 * When our assessment is "no deficiency" (`ourIsDeficiency` false), severity
 * doesn't apply — the client either affirms clean (agree) or says we missed one
 * (reject).
 */
export function VerdictControls({
  onSubmit,
  busy,
  ourIsDeficiency = true,
}: {
  onSubmit: (d: {
    clientVerdict?: "agree" | "severity_diff" | "reject";
    clientSeverity?: Severity;
    clientCantAssess?: boolean;
    reason?: string;
  }) => void;
  busy?: boolean;
  ourIsDeficiency?: boolean;
}) {
  const [mode, setMode] = useState<"idle" | "severity">("idle");

  const cantTell = (
    <>
      <Button variant="ghost" disabled={busy} onClick={() => onSubmit({ clientCantAssess: true })} className="text-muted-foreground">
        Can't tell — poor image
      </Button>
      {/* Distinct skip for miscategorized nameplate/tag photos (see ScratchControls). */}
      <Button
        variant="ghost"
        disabled={busy}
        onClick={() => onSubmit({ clientCantAssess: true, reason: "[tag/label photo — not judgeable]" })}
        className="text-muted-foreground"
      >
        It's an ID tag/label — skip
      </Button>
    </>
  );

  if (!ourIsDeficiency) {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => onSubmit({ clientVerdict: "agree" })}
          className="border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 hover:text-emerald-700"
        >
          Agree — looks fine
        </Button>
        <Button
          variant="outline"
          disabled={busy}
          onClick={() => onSubmit({ clientVerdict: "reject" })}
          className="border-rose-500/40 text-rose-700 hover:bg-rose-500/10 hover:text-rose-700"
        >
          Actually, there's a deficiency
        </Button>
        {cantTell}
      </div>
    );
  }

  if (mode === "severity") {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">What's the right severity?</p>
        <SeverityPicker disabled={busy} onPick={(s) => onSubmit({ clientVerdict: "severity_diff", clientSeverity: s })} />
        <Button variant="ghost" size="sm" onClick={() => setMode("idle")}>
          Back
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      <Button
        variant="outline"
        disabled={busy}
        onClick={() => onSubmit({ clientVerdict: "agree" })}
        className="border-emerald-500/40 text-emerald-700 hover:bg-emerald-500/10 hover:text-emerald-700"
      >
        Agree
      </Button>
      <Button
        variant="outline"
        disabled={busy}
        onClick={() => setMode("severity")}
        className="border-amber-500/40 text-amber-700 hover:bg-amber-500/10 hover:text-amber-700"
      >
        Right, but wrong severity
      </Button>
      <Button
        variant="outline"
        disabled={busy}
        onClick={() => onSubmit({ clientVerdict: "reject" })}
        className="border-rose-500/40 text-rose-700 hover:bg-rose-500/10 hover:text-rose-700"
      >
        Not a deficiency
      </Button>
      {cantTell}
    </div>
  );
}
